import React, { useState, useEffect, useRef, useCallback } from 'react';
import Modal from './Modal';
import './Modal.css';
import './IntegrationFormModal.css';
import { adminAPI } from '../../services/api';

interface IntegrationFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  integration: any | null;
}

const normalizeCredential = (value: unknown) => String(value || '').trim();

const IntegrationFormModal: React.FC<IntegrationFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  integration
}) => {
  const [formData, setFormData] = useState({
    service_name: '',
    integration_type: 'PAYMENT',
    display_name: '',
    description: '',
    api_key: '',
    api_secret: '',
    environment: 'SANDBOX',
    webhook_url: '',
    config: {} as Record<string, any>,
    is_active: true
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [connectionTesting, setConnectionTesting] = useState<'shipping' | 'fraud' | null>(null);
  const [connectionResults, setConnectionResults] = useState<Record<string, { success: boolean; message: string }>>({});
  const connectionSequence = useRef(0);
  const invalidateConnectionTests = useCallback(() => { ++connectionSequence.current; }, []);
  const isSteadfast = formData.integration_type === 'SHIPPING' && formData.service_name === 'steadfast';
  const savedCredentialsChanged = !!integration && (
    normalizeCredential(formData.api_key) !== normalizeCredential(integration.api_key) || !!formData.api_secret.trim() ||
    normalizeCredential(formData.config.login_email) !== normalizeCredential(integration.config?.login_email) ||
    normalizeCredential(formData.config.login_password) !== normalizeCredential(integration.config?.login_password) ||
    formData.is_active !== integration.is_active
  );

  const testConnection = async (kind: 'shipping' | 'fraud') => {
    if (!integration?.id || savedCredentialsChanged || !formData.is_active) return;
    const request = ++connectionSequence.current;
    setConnectionTesting(kind);
    setConnectionResults(previous => {
      const next = { ...previous };
      delete next[kind];
      return next;
    });
    try {
      const response = kind === 'shipping'
        ? await adminAPI.testIntegrationShippingConnection(integration.id)
        : await adminAPI.testIntegrationFraudConnection(integration.id);
      if (request !== connectionSequence.current) return;
      const success = response.data?.status === 'success';
      setConnectionResults(previous => ({
        ...previous,
        [kind]: {
          success,
          message: response.data?.message || (success ? 'Connection verified.' : 'Connection could not be verified.'),
        },
      }));
    } catch (error: any) {
      if (request !== connectionSequence.current) return;
      setConnectionResults(previous => ({
        ...previous,
        [kind]: { success: false, message: error?.response?.data?.message || 'Connection test failed. Please retry.' },
      }));
    } finally {
      if (request === connectionSequence.current) setConnectionTesting(null);
    }
  };

  useEffect(() => {
    invalidateConnectionTests();
    setConnectionTesting(null);
    setConnectionResults({});
    return invalidateConnectionTests;
  }, [integration, isOpen, savedCredentialsChanged, invalidateConnectionTests]);

  const copyToClipboard = (text: string, fieldName: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Police station dropdown state
  const [policeStations, setPoliceStations] = useState<any[]>([]);
  const [psLoading, setPsLoading] = useState(false);
  const [psSearch, setPsSearch] = useState('');
  const [psDropdownOpen, setPsDropdownOpen] = useState(false);
  const psDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch police stations when service is steadfast
  const fetchPoliceStations = useCallback(async () => {
    if (policeStations.length > 0) return; // already loaded
    setPsLoading(true);
    try {
      const resp = await adminAPI.getSteadfastPoliceStations();
      setPoliceStations(resp.data.districts || []);
    } catch (err) {
      console.error('Failed to load police stations:', err);
    } finally {
      setPsLoading(false);
    }
  }, [policeStations.length]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (psDropdownRef.current && !psDropdownRef.current.contains(e.target as Node)) {
        setPsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Load police stations when steadfast form is shown
  useEffect(() => {
    if (formData.integration_type === 'SHIPPING' && formData.service_name === 'steadfast' && isOpen) {
      fetchPoliceStations();
    }
  }, [formData.integration_type, formData.service_name, isOpen, fetchPoliceStations]);

  // Helper: find selected station name from ID
  const getSelectedStationLabel = (): string => {
    const id = formData.config.pickup_police_station_id;
    if (!id) return '';
    for (const district of policeStations) {
      for (const ps of (district.policestations || [])) {
        if (ps.id === id) return `${ps.name} — ${district.name}`;
      }
    }
    return `Station ID: ${id}`;
  };

  // Function to generate webhook URL based on service
  const getWebhookUrl = (serviceName: string): string => {
    if (!serviceName) return '';
    
    // Determine base URL based on environment (dynamic origin for live domains)
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const baseUrl = isDevelopment ? 'http://127.0.0.1:8000' : window.location.origin;
    
    // Map service names to webhook endpoints
    const webhookMap: Record<string, string> = {
      // Payment Gateways
      'sslcommerz': '/api/webhooks/sslcommerz/',
      'bkash': '/api/webhooks/bkash/',
      'nagad': '/api/webhooks/nagad/',
      'stripe': '/api/webhooks/stripe/',
      // Shipping Providers
      'pathao': '/api/webhooks/pathao/',
      'redx': '/api/webhooks/redx/',
      'sundarban': '/api/webhooks/sundarban/',
      'steadfast': '/api/webhooks/steadfast/',
      // Email Services
      'sendgrid': '/api/webhooks/sendgrid/',
      'mailgun': '/api/webhooks/mailgun/',
      'smtp': '', // SMTP doesn't need webhook
      // Analytics
      'google_analytics': '' // GA doesn't need webhook
    };
    
    const webhookPath = webhookMap[serviceName];
    return webhookPath ? `${baseUrl}${webhookPath}` : '';
  };

  // Integration type options
  const integrationTypes = [
    { value: 'PAYMENT', label: 'Payment Gateway' },
    { value: 'SHIPPING', label: 'Shipping Provider' },
    { value: 'ANALYTICS', label: 'Analytics Service' },
    { value: 'EMAIL', label: 'Email Service' }
  ];

  // Service options based on integration type
  const serviceOptions: Record<string, Array<{value: string, label: string}>> = {
    PAYMENT: [
      { value: 'sslcommerz', label: 'SSLCommerz' },
      { value: 'bkash', label: 'bKash' },
      { value: 'nagad', label: 'Nagad' },
      { value: 'stripe', label: 'Stripe' }
    ],
    SHIPPING: [
      { value: 'pathao', label: 'Pathao' },
      { value: 'redx', label: 'RedX' },
      { value: 'sundarban', label: 'Sundarban Courier' },
      { value: 'steadfast', label: 'Steadfast Courier' }
    ],
    ANALYTICS: [
      { value: 'google_analytics', label: 'Google Analytics' }
    ],
    EMAIL: [
      { value: 'smtp', label: 'SMTP' },
      { value: 'sendgrid', label: 'SendGrid' },
      { value: 'mailgun', label: 'Mailgun' }
    ]
  };

  // Environment options
  const environmentOptions = [
    { value: 'SANDBOX', label: 'Sandbox/Test' },
    { value: 'PRODUCTION', label: 'Production/Live' }
  ];

  useEffect(() => {
    if (integration) {
      setFormData({
        service_name: integration.service_name || '',
        integration_type: integration.integration_type || 'PAYMENT',
        display_name: integration.display_name || '',
        description: integration.description || '',
        api_key: integration.api_key || '',
        api_secret: '', // Don't populate for security
        environment: integration.environment || 'SANDBOX',
        webhook_url: integration.webhook_url || '',
        config: integration.config || {},
        is_active: integration.is_active !== undefined ? integration.is_active : true
      });
    } else {
      // Reset form for new integration
      setFormData({
        service_name: '',
        integration_type: 'PAYMENT',
        display_name: '',
        description: '',
        api_key: '',
        api_secret: '',
        environment: 'SANDBOX',
        webhook_url: '',
        config: {},
        is_active: true
      });
    }
    setErrors({});
  }, [integration, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      // Auto-populate webhook URL when service_name changes
      if (name === 'service_name' && value) {
        newData.webhook_url = getWebhookUrl(value as string);
      }
      
      return newData;
    });
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleConfigChange = (key: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      config: { ...prev.config, [key]: value }
    }));
    if (key === 'login_email' || key === 'login_password') {
      setErrors(previous => ({ ...previous, config_login: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.service_name.trim()) {
      newErrors.service_name = 'Service name is required';
    }
    if (!formData.display_name.trim()) {
      newErrors.display_name = 'Display name is required';
    }
    if (!formData.api_key.trim()) {
      newErrors.api_key = 'API Key is required';
    }

    // Validate based on integration type
    if (formData.integration_type === 'PAYMENT' && formData.service_name === 'sslcommerz') {
      if (!formData.config.store_id) {
        newErrors.config_store_id = 'Store ID is required for SSLCommerz';
      }
    }

    if (formData.integration_type === 'EMAIL' && formData.service_name === 'smtp') {
      if (!formData.config.host) {
        newErrors.config_host = 'SMTP Host is required';
      }
      if (!formData.config.port) {
        newErrors.config_port = 'SMTP Port is required';
      }
    }

    if (formData.integration_type === 'SHIPPING' && ['steadfast', 'pathao'].includes(formData.service_name)) {
      const hasLogin = !!String(formData.config.login_email || '').trim();
      const hasPassword = !!String(formData.config.login_password || '').trim();
      if (hasLogin !== hasPassword) {
        newErrors.config_login = 'Enter both portal login and password for fraud checks, or leave both blank to use shipping only.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);
      // Clean up form data before sending
      const cleanedData: any = {
        ...formData,
        // Convert empty webhook_url to null
        webhook_url: formData.webhook_url?.trim() || null,
      };
      
      // Don't send api_secret if empty during edit (keeps existing value)
      if (integration && !formData.api_secret?.trim()) {
        delete cleanedData.api_secret;
      }
      
      await onSave(cleanedData);
      onClose();
    } catch (error: any) {
      console.error('Save error:', error);
      if (error.response?.data) {
        const apiErrors: Record<string, string> = {};
        Object.keys(error.response.data).forEach(key => {
          apiErrors[key] = Array.isArray(error.response.data[key])
            ? error.response.data[key][0]
            : error.response.data[key];
        });
        setErrors(apiErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  // Render integration-specific config fields
  const renderConfigFields = () => {
    const { integration_type, service_name } = formData;

    if (integration_type === 'PAYMENT' && service_name === 'sslcommerz') {
      return (
        <div className="form-table-row">
          <div className="form-label-cell">Store ID *</div>
          <div className="form-input-cell">
            <input
              type="text"
              value={formData.config.store_id || ''}
              onChange={(e) => handleConfigChange('store_id', e.target.value)}
              placeholder="Enter SSLCommerz Store ID"
              className={errors.config_store_id ? 'error' : ''}
            />
            {errors.config_store_id && <span className="error-message">{errors.config_store_id}</span>}
          </div>
        </div>
      );
    }

    if (integration_type === 'PAYMENT' && service_name === 'bkash') {
      return (
        <>
          <div className="form-table-row">
            <div className="form-label-cell">App Key</div>
            <div className="form-input-cell">
              <input
                type="text"
                value={formData.config.app_key || ''}
                onChange={(e) => handleConfigChange('app_key', e.target.value)}
                placeholder="Enter bKash App Key"
              />
            </div>
          </div>
          <div className="form-table-row">
            <div className="form-label-cell">Username</div>
            <div className="form-input-cell">
              <input
                type="text"
                value={formData.config.username || ''}
                onChange={(e) => handleConfigChange('username', e.target.value)}
                placeholder="Enter bKash Username"
              />
            </div>
          </div>
        </>
      );
    }

    if (integration_type === 'SHIPPING' && service_name === 'pathao') {
      return (
        <>
          <div className="form-table-row">
            <div className="form-label-cell">Store ID</div>
            <div className="form-input-cell">
              <input
                type="text"
                value={formData.config.store_id || ''}
                onChange={(e) => handleConfigChange('store_id', e.target.value)}
                placeholder="Enter Pathao Store ID"
              />
            </div>
          </div>
          <div className="form-table-row">
            <div className="form-label-cell" style={{ fontWeight: 600, color: '#e85d04', fontSize: '0.85rem', borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
              ── Fraud Check Login ──
            </div>
            <div className="form-input-cell" style={{ fontSize: '0.78rem', color: '#888', borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
              Optional: enter both merchant portal credentials to enable customer fraud checking. Shipping can be used without them.
            </div>
          </div>
          <div className="form-table-row">
            <div className="form-label-cell">Login Email / Phone</div>
            <div className="form-input-cell">
              <input
                type="text"
                value={formData.config.login_email || ''}
                onChange={(e) => handleConfigChange('login_email', e.target.value)}
                placeholder="Pathao merchant login email or phone"
              />
              <small style={{ color: '#888', fontSize: '0.75rem' }}>Same credentials you use to login at merchant.pathao.com</small>
            </div>
          </div>
          <div className="form-table-row">
            <div className="form-label-cell">Login Password</div>
            <div className="form-input-cell">
              <input
                type="password"
                value={formData.config.login_password || ''}
                onChange={(e) => handleConfigChange('login_password', e.target.value)}
                placeholder="Pathao merchant login password"
              />
              {errors.config_login && <span className="error-message" role="alert">{errors.config_login}</span>}
            </div>
          </div>
        </>
      );
    }

    if (integration_type === 'SHIPPING' && service_name === 'steadfast') {
      return (
        <>
          <div className="form-table-row">
            <div className="form-label-cell" style={{ fontWeight: 600, color: '#6c3fbf', fontSize: '0.85rem', borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
              ── Merchant Account Info ──
            </div>
            <div className="form-input-cell" style={{ fontSize: '0.78rem', color: '#888', borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
              Displayed on thermal shipping labels alongside Consignment ID
            </div>
          </div>
          <div className="form-table-row">
            <div className="form-label-cell">Steadfast User ID</div>
            <div className="form-input-cell">
              <input
                type="text"
                value={formData.config?.user_id || formData.config?.steadfast_user_id || ''}
                onChange={(e) => {
                  const val = e.target.value.trim();
                  setFormData(prev => ({
                    ...prev,
                    config: {
                      ...(prev.config || {}),
                      user_id: val,
                      steadfast_user_id: val
                    }
                  }));
                }}
                placeholder="e.g., J9SH1IEJ (from Steadfast dashboard)"
              />
              <small style={{ color: '#888', fontSize: '0.75rem' }}>Your Steadfast merchant User ID (e.g. <strong>J9SH1IEJ</strong>) printed directly on thermal shipping labels</small>
            </div>
          </div>
          <div className="form-table-row">
            <div className="form-label-cell" style={{ fontWeight: 600, color: '#6c3fbf', fontSize: '0.85rem', borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
              ── Pickup Settings ──
            </div>
            <div className="form-input-cell" style={{ fontSize: '0.78rem', color: '#888', borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
              Required for Pickup Request feature
            </div>
          </div>
          <div className="form-table-row">
            <div className="form-label-cell">Pickup Address ID *</div>
            <div className="form-input-cell">
              <input
                type="number"
                value={formData.config.pickup_address_id || ''}
                onChange={(e) => handleConfigChange('pickup_address_id', e.target.value ? parseInt(e.target.value) : '')}
                placeholder="e.g., 1 (from Steadfast portal)"
                min="1"
              />
              <small style={{ color: '#888', fontSize: '0.75rem' }}>Your merchant address ID from Steadfast</small>
            </div>
          </div>
          <div className="form-table-row">
            <div className="form-label-cell">Police Station *</div>
            <div className="form-input-cell">
              <div ref={psDropdownRef} style={{ position: 'relative' }}>
                {/* Selected value / search input */}
                <div
                  onClick={() => { setPsDropdownOpen(true); setPsSearch(''); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    border: '1px solid #d1d5db', borderRadius: '6px', padding: '7px 10px',
                    cursor: 'pointer', background: '#fff', minHeight: '36px',
                    ...(psDropdownOpen ? { borderColor: '#6c3fbf', boxShadow: '0 0 0 2px rgba(108,63,191,0.15)' } : {})
                  }}
                >
                  {psDropdownOpen ? (
                    <input
                      autoFocus
                      type="text"
                      value={psSearch}
                      onChange={(e) => setPsSearch(e.target.value)}
                      placeholder="Search district or thana..."
                      style={{
                        border: 'none', outline: 'none', flex: 1, fontSize: '0.875rem',
                        background: 'transparent', padding: 0, width: '100%'
                      }}
                    />
                  ) : (
                    <span style={{ flex: 1, fontSize: '0.875rem', color: formData.config.pickup_police_station_id ? '#111' : '#9ca3af' }}>
                      {formData.config.pickup_police_station_id ? getSelectedStationLabel() : 'Select police station / thana...'}
                    </span>
                  )}
                  <span style={{ fontSize: '10px', color: '#9ca3af', flexShrink: 0 }}>{psDropdownOpen ? '▲' : '▼'}</span>
                </div>

                {/* Dropdown list */}
                {psDropdownOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
                    background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: '280px',
                    overflowY: 'auto', marginTop: '4px'
                  }}>
                    {psLoading ? (
                      <div style={{ padding: '16px', textAlign: 'center', color: '#888', fontSize: '0.85rem' }}>
                        ⏳ Loading police stations...
                      </div>
                    ) : policeStations.length === 0 ? (
                      <div style={{ padding: '16px', textAlign: 'center', color: '#888', fontSize: '0.85rem' }}>
                        No data. Save API credentials first, then reopen.
                      </div>
                    ) : (
                      policeStations
                        .map(district => {
                          const searchLower = psSearch.toLowerCase();
                          const filteredStations = (district.policestations || []).filter((ps: any) =>
                            ps.name.toLowerCase().includes(searchLower) ||
                            district.name.toLowerCase().includes(searchLower)
                          );
                          if (filteredStations.length === 0) return null;
                          return (
                            <div key={district.id}>
                              <div style={{
                                padding: '6px 12px', fontSize: '0.72rem', fontWeight: 700,
                                color: '#6c3fbf', background: '#f5f3ff',
                                position: 'sticky', top: 0, borderBottom: '1px solid #ede9fe',
                                textTransform: 'uppercase', letterSpacing: '0.5px'
                              }}>
                                {district.name}
                              </div>
                              {filteredStations.map((ps: any) => (
                                <div
                                  key={ps.id}
                                  onClick={() => {
                                    handleConfigChange('pickup_police_station_id', ps.id);
                                    setPsDropdownOpen(false);
                                    setPsSearch('');
                                  }}
                                  style={{
                                    padding: '8px 12px 8px 20px', cursor: 'pointer',
                                    fontSize: '0.85rem', color: '#374151',
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    background: formData.config.pickup_police_station_id === ps.id ? '#ede9fe' : 'transparent',
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f3f4f6')}
                                  onMouseLeave={(e) => (e.currentTarget.style.background = formData.config.pickup_police_station_id === ps.id ? '#ede9fe' : 'transparent')}
                                >
                                  <span>{ps.name}</span>
                                  <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>ID: {ps.id}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })
                        .filter(Boolean)
                    )}
                    {!psLoading && policeStations.length > 0 && psSearch && policeStations.every(d =>
                      (d.policestations || []).every((ps: any) =>
                        !ps.name.toLowerCase().includes(psSearch.toLowerCase()) &&
                        !d.name.toLowerCase().includes(psSearch.toLowerCase())
                      )
                    ) && (
                      <div style={{ padding: '14px', textAlign: 'center', color: '#888', fontSize: '0.85rem' }}>
                        No matching stations for "{psSearch}"
                      </div>
                    )}
                  </div>
                )}
              </div>
              <small style={{ color: '#888', fontSize: '0.75rem' }}>
                {formData.config.pickup_police_station_id
                  ? `Selected ID: ${formData.config.pickup_police_station_id}`
                  : 'Search and select your pickup area thana'}
              </small>
            </div>
          </div>
          <div className="form-table-row">
            <div className="form-label-cell">Pickup Address *</div>
            <div className="form-input-cell">
              <input
                type="text"
                value={formData.config.pickup_address || ''}
                onChange={(e) => handleConfigChange('pickup_address', e.target.value)}
                placeholder="Full pickup location address"
              />
            </div>
          </div>
          <div className="form-table-row">
            <div className="form-label-cell">Contact Number *</div>
            <div className="form-input-cell">
              <input
                type="text"
                value={formData.config.pickup_contact_number || ''}
                onChange={(e) => handleConfigChange('pickup_contact_number', e.target.value)}
                placeholder="e.g., 01XXXXXXXXX"
              />
            </div>
          </div>
          <div className="form-table-row">
            <div className="form-label-cell">Pickup Note</div>
            <div className="form-input-cell">
              <input
                type="text"
                value={formData.config.pickup_note || ''}
                onChange={(e) => handleConfigChange('pickup_note', e.target.value)}
                placeholder="Optional note for the rider"
              />
            </div>
          </div>
          {/* Fraud Check Section */}
          <div className="form-table-row">
            <div className="form-label-cell" style={{ fontWeight: 600, color: '#dc2626', fontSize: '0.85rem', borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
              ── Fraud Check Login ──
            </div>
            <div className="form-input-cell" style={{ fontSize: '0.78rem', color: '#888', borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
              Optional: enter both portal credentials for customer fraud checks. Shipping API credentials alone do not enable fraud statistics.
            </div>
          </div>
          <div className="form-table-row">
            <div className="form-label-cell">Login Email</div>
            <div className="form-input-cell">
              <input
                type="text"
                value={formData.config.login_email || ''}
                onChange={(e) => handleConfigChange('login_email', e.target.value)}
                placeholder="steadfast.com.bd login email"
              />
              <small style={{ color: '#888', fontSize: '0.75rem' }}>Same credentials you use to login at steadfast.com.bd portal</small>
            </div>
          </div>
          <div className="form-table-row">
            <div className="form-label-cell">Login Password</div>
            <div className="form-input-cell">
              <input
                type="password"
                value={formData.config.login_password || ''}
                onChange={(e) => handleConfigChange('login_password', e.target.value)}
                placeholder="steadfast.com.bd login password"
              />
              {errors.config_login && <span className="error-message" role="alert">{errors.config_login}</span>}
            </div>
          </div>
          {/* Webhook Configuration Guide Section */}
          <div className="form-table-row">
            <div className="form-label-cell" style={{ fontWeight: 600, color: '#059669', fontSize: '0.85rem', borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
              ── Webhook Setup ──
            </div>
            <div className="form-input-cell" style={{ fontSize: '0.78rem', color: '#888', borderBottom: '1px solid #e5e7eb', paddingBottom: 6 }}>
              Copy & paste these into your Steadfast Merchant Portal under <strong>Settings → Webhook Integration</strong>
            </div>
          </div>
          <div className="form-table-row">
            <div className="form-label-cell">Callback URL</div>
            <div className="form-input-cell">
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <input
                  type="text"
                  readOnly
                  value={formData.webhook_url || getWebhookUrl('steadfast')}
                  style={{ backgroundColor: '#f8fafc', fontFamily: 'monospace', fontSize: '0.8rem', color: '#334155' }}
                />
                <button
                  type="button"
                  onClick={() => copyToClipboard(formData.webhook_url || getWebhookUrl('steadfast'), 'webhook_url')}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: copiedField === 'webhook_url' ? '#dcfce7' : '#f3f4f6',
                    color: copiedField === 'webhook_url' ? '#15803d' : '#374151',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s'
                  }}
                >
                  {copiedField === 'webhook_url' ? '✓ Copied' : '📋 Copy'}
                </button>
              </div>
              <small style={{ color: '#059669', fontSize: '0.75rem' }}>Paste this as "Callback Url" on Steadfast</small>
            </div>
          </div>
          <div className="form-table-row">
            <div className="form-label-cell">Auth Token (Bearer)</div>
            <div className="form-input-cell">
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <input
                  type="text"
                  readOnly
                  value={formData.api_key || ''}
                  placeholder="Enter API Key above first"
                  style={{ backgroundColor: '#f8fafc', fontFamily: 'monospace', fontSize: '0.8rem', color: '#334155' }}
                />
                <button
                  type="button"
                  disabled={!formData.api_key}
                  onClick={() => copyToClipboard(formData.api_key, 'bearer_token')}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    backgroundColor: copiedField === 'bearer_token' ? '#dcfce7' : !formData.api_key ? '#f1f5f9' : '#f3f4f6',
                    color: copiedField === 'bearer_token' ? '#15803d' : !formData.api_key ? '#94a3b8' : '#374151',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    cursor: !formData.api_key ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s'
                  }}
                >
                  {copiedField === 'bearer_token' ? '✓ Copied' : '📋 Copy'}
                </button>
              </div>
              <small style={{ color: '#059669', fontSize: '0.75rem' }}>Paste this as "Auth Token(Bearer)" on Steadfast</small>
            </div>
          </div>
        </>
      );
    }

    if (integration_type === 'EMAIL' && service_name === 'smtp') {
      return (
        <>
          <div className="form-table-row">
            <div className="form-label-cell">SMTP Host *</div>
            <div className="form-input-cell">
              <input
                type="text"
                value={formData.config.host || ''}
                onChange={(e) => handleConfigChange('host', e.target.value)}
                placeholder="e.g., smtp.gmail.com"
                className={errors.config_host ? 'error' : ''}
              />
              {errors.config_host && <span className="error-message">{errors.config_host}</span>}
            </div>
          </div>
          <div className="form-table-row">
            <div className="form-label-cell">SMTP Port *</div>
            <div className="form-input-cell">
              <input
                type="number"
                value={formData.config.port || ''}
                onChange={(e) => handleConfigChange('port', parseInt(e.target.value))}
                placeholder="e.g., 587"
                className={errors.config_port ? 'error' : ''}
              />
              {errors.config_port && <span className="error-message">{errors.config_port}</span>}
            </div>
          </div>
          <div className="form-table-row checkbox-row">
            <div className="form-label-cell">Use TLS</div>
            <div className="form-input-cell">
              <label className="toggle-switch-label">
                <input
                  type="checkbox"
                  checked={formData.config.use_tls || false}
                  onChange={(e) => handleConfigChange('use_tls', e.target.checked)}
                />
                <span>Enable TLS Encryption</span>
              </label>
            </div>
          </div>
        </>
      );
    }

    if (integration_type === 'ANALYTICS' && service_name === 'google_analytics') {
      return (
        <div className="form-table-row">
          <div className="form-label-cell">Tracking ID</div>
          <div className="form-input-cell">
            <input
              type="text"
              value={formData.config.tracking_id || ''}
              onChange={(e) => handleConfigChange('tracking_id', e.target.value)}
              placeholder="e.g., UA-XXXXXXXXX-X or G-XXXXXXXXXX"
            />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={integration ? 'Edit Integration' : 'Add New Integration'}>
      <form onSubmit={handleSubmit} className="integration-form">
        <div className="integration-form-table">
          {/* Integration Type */}
          <div className="form-table-row">
            <div className="form-label-cell">Integration Type *</div>
            <div className="form-input-cell">
              <select
                name="integration_type"
                value={formData.integration_type}
                onChange={handleChange}
                disabled={!!integration}
              >
                {integrationTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Service */}
          <div className="form-table-row">
            <div className="form-label-cell">Service *</div>
            <div className="form-input-cell">
              <select
                name="service_name"
                value={formData.service_name}
                onChange={handleChange}
                disabled={!!integration}
                className={errors.service_name ? 'error' : ''}
              >
                <option value="">Select Service</option>
                {serviceOptions[formData.integration_type]?.map(service => (
                  <option key={service.value} value={service.value}>{service.label}</option>
                ))}
              </select>
              {errors.service_name && <span className="error-message">{errors.service_name}</span>}
            </div>
          </div>

          {/* Display Name */}
          <div className="form-table-row">
            <div className="form-label-cell">Display Name *</div>
            <div className="form-input-cell">
              <input
                type="text"
                name="display_name"
                value={formData.display_name}
                onChange={handleChange}
                placeholder="e.g., SSLCommerz Payment Gateway"
                className={errors.display_name ? 'error' : ''}
              />
              {errors.display_name && <span className="error-message">{errors.display_name}</span>}
            </div>
          </div>

          {/* Description */}
          <div className="form-table-row">
            <div className="form-label-cell">Description</div>
            <div className="form-input-cell">
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Optional description"
                rows={3}
              />
            </div>
          </div>

          {/* API Credentials */}
          <div className="form-table-row">
            <div className="form-label-cell">API Key *</div>
            <div className="form-input-cell">
              <input
                type="text"
                name="api_key"
                value={formData.api_key}
                onChange={handleChange}
                placeholder="Enter API Key or Client ID"
                className={errors.api_key ? 'error' : ''}
              />
              {errors.api_key && <span className="error-message">{errors.api_key}</span>}
            </div>
          </div>

          <div className="form-table-row">
            <div className="form-label-cell">API Secret</div>
            <div className="form-input-cell">
              <input
                type="password"
                name="api_secret"
                value={formData.api_secret}
                onChange={handleChange}
                placeholder={integration ? "Leave blank to keep current" : "Enter API Secret"}
              />
            </div>
          </div>

          {isSteadfast && (
            <div className="form-table-row">
              <div className="form-label-cell">Steadfast connections</div>
              <div className="form-input-cell">
                <p style={{ margin: '0 0 8px', fontSize: '0.8rem' }}>
                  Shipping uses your API key and secret. Fraud statistics use your Steadfast portal login email and password below.
                  {' '}Save credentials before testing. Configuration means credentials are saved; a connection test verifies access.
                </p>
                {!integration?.id ? (
                  <small>Create the integration, then reopen it to test the saved connections.</small>
                ) : (
                  <>
                    {(['shipping', 'fraud'] as const).map(kind => {
                      const configured = kind === 'shipping' ? integration.shipping_configured : integration.fraud_configured;
                      const result = connectionResults[kind];
                      return (
                        <div key={kind} style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: '0.82rem', marginBottom: 5 }}>
                            <strong>{kind === 'shipping' ? 'Shipping' : 'Fraud'}: {configured ? 'Configured' : 'Not configured'}</strong>
                            {' · '}{result?.success && !savedCredentialsChanged ? 'Verified this session' : 'Not verified'}
                          </div>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => { void testConnection(kind); }}
                            disabled={loading || !!connectionTesting || !configured || !formData.is_active || savedCredentialsChanged}
                          >
                            {connectionTesting === kind ? 'Testing...' : kind === 'shipping' ? 'Test Shipping Connection' : 'Test Fraud Login'}
                          </button>
                          {result && !savedCredentialsChanged && (
                            <p role="status" style={{ fontSize: '0.8rem', color: result.success ? '#15803d' : '#dc2626', margin: '5px 0 0' }}>
                              {result.message}
                            </p>
                          )}
                        </div>
                      );
                    })}
                    {!integration.fraud_configured && <p style={{ fontSize: '0.8rem' }}>To enable automatic fraud statistics in orders, add the portal login email and password, save, then test Fraud Login.</p>}
                    {!formData.is_active && <small>Activate and save this integration to enable the connections.</small>}
                    {savedCredentialsChanged && <small>Save changes, then reopen to test the updated credentials.</small>}
                    <p style={{ fontSize: '0.75rem', margin: '6px 0 0' }}>The fraud login test checks portal access without looking up a customer. Customer history may still be limited by Steadfast.</p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Environment */}
          <div className="form-table-row">
            <div className="form-label-cell">Environment *</div>
            <div className="form-input-cell">
              <select
                name="environment"
                value={formData.environment}
                onChange={handleChange}
              >
                {environmentOptions.map(env => (
                  <option key={env.value} value={env.value}>{env.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Webhook URL */}
          <div className="form-table-row">
            <div className="form-label-cell">Webhook URL</div>
            <div className="form-input-cell">
              <input
                type="url"
                name="webhook_url"
                value={formData.webhook_url}
                onChange={handleChange}
                placeholder="Auto-generated based on service"
                readOnly
                style={{ backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}
                className={errors.webhook_url ? 'error' : ''}
              />
              {formData.webhook_url && (
                <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                  ✅ Webhook URL auto-generated for {formData.service_name}
                </small>
              )}
              {errors.webhook_url && <span className="error-message">{errors.webhook_url}</span>}
            </div>
          </div>

          {/* Integration-specific config fields */}
          {renderConfigFields()}

          {/* Active Status */}
          <div className="form-table-row checkbox-row">
            <div className="form-label-cell">Status</div>
            <div className="form-input-cell">
              <label className="toggle-switch-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                <span>Active Integration</span>
              </label>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button type="button" onClick={onClose} className="btn-secondary" disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Saving...' : (integration ? 'Update' : 'Create')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default IntegrationFormModal;

